import {
  MIN_PLAYERS, MAX_PLAYERS, COLUMNS, PHASES, TURN_PHASES,
} from '../constants';
import { createRngState, nextInt } from '../rng/rng';

/** A fresh turn: no runners out, no dice showing, roll to begin. */
export const freshTurn = () => ({
  runners: {},        // { column: step } - the white cubes, this turn only
  dice: null,         // the four dice currently showing, or null
  options: null,      // legal plays for those dice (while phase is 'choosing')
  phase: TURN_PHASES.ROLLING,
  moved: false,       // has any runner advanced this turn? gates Stop
});

/**
 * Builds a new game.
 *
 * `players` is an array of { name, isBot } - 2 to 4 of them ("Each player
 * chooses a color (red, green, blue, or yellow)", rules.pdf p1, Setup).
 *
 * Board state lives in two places, deliberately not one:
 *  - `columns` is the board's own memory: which columns are claimed and by
 *    whom. `{ [column]: playerId | null }`.
 *  - each player's `markers` are their colored cubes: `{ [column]: step }`,
 *    absent key = not on that column. Several players' markers share spaces
 *    freely (p2, "several players' cubes can occupy the same space") so
 *    nothing about one player's markers constrains another's.
 *
 * "Players roll two dice to see who goes first" (p1, Setup) - a digital
 * table draws the seat from the seeded RNG instead, so a seed still
 * reproduces the whole game exactly.
 */
export const createGameState = ({ players, seed = Date.now(), startingPlayerIndex = null } = {}) => {
  if (!Array.isArray(players) || players.length < MIN_PLAYERS || players.length > MAX_PLAYERS) {
    throw new Error(`Can't Stop needs ${MIN_PLAYERS}-${MAX_PLAYERS} players, got ${players?.length ?? 0}`);
  }

  let rngState = createRngState(seed);

  let firstSeat = startingPlayerIndex;
  if (firstSeat === null || firstSeat === undefined) {
    const [pick, afterPick] = nextInt(players.length, rngState);
    rngState = afterPick;
    firstSeat = pick;
  }

  const columns = {};
  COLUMNS.forEach((column) => { columns[column] = null; });

  return {
    meta: {
      phase: PHASES.PLAYING,
      activePlayerIndex: firstSeat,
      startingPlayerIndex: firstSeat,
      winnerId: null,
      seed,
      rngState,
      turn: 1,
      // Monotonic action counter. Local play never reads it; online play
      // writes state back only if the copy it started from is still the
      // current one, so two clients can't both apply the same action (see
      // src/store/multiplayer.js).
      seq: 0,
    },
    columns,
    turn: freshTurn(),
    players: players.map((player, index) => ({
      id: player.id ?? `p${index}`,
      name: player.name || `Player ${index + 1}`,
      isBot: Boolean(player.isBot),
      seat: index,
      markers: {},
    })),
    log: [{
      kind: 'gameStart',
      playerCount: players.length,
      firstPlayer: players[firstSeat]?.name,
    }],
  };
};
